import { gql } from '@apollo/client'

export const CONTENT_LIST_QUERY = gql`
  query ContentList(
    $offset: Int = 0
    $limit: Int = 25
    $order: [[String]]
    $where: ContentWhereInput = {}
    $language: [String] = ["en", "_primary"]
  ) {
    contentList(offset: $offset, limit: $limit, order: $order, where: $where, language: $language) {
      count
      limit
      offset
      content {
        titleGroupId
        contentId
        contentType
        language
        title
      }
    }
  }
`

export const GET_COURSE_CONTENT = gql`
  query GetCourseContent($id: ID!) {
    contentList(where: { id: { eq: $id }, contentType: { eq: "course" } }) {
      content {
        id
        contentId
        contentType
        title
        description
        descriptionFormat
        shortDescription
        shortDescriptionFormat
        thirdPartyDescription
        path
        language
        primaryLanguage
        published
        publishedAtDate
        productionStatus
        contentSearchNoIndex
        excludeFromRecommendations
        createdAt
        updatedAt
        productType
        displayType
        seasonNumber
        episodeNumber
        director
        producer
        writer
        studio
        copyright
        instructor
        cast
        previewMediaId
        featureMediaId
        contentReferencesTo(types: ["course-module"]) {
          sortKey
          contentFrom {
            id
            contentId
            contentType
            title
            contentReferencesTo(types: ["course-module-practice", "course-module-other"]) {
              contentReferenceType {
                type
              }
              sortKey
            }
          }
        }
        contentReferencesFrom(
          types: [
            "admin_category"
            "category"
            "rainbow-category"
            "centers"
            "marketing-centers"
            "content_origin"
            "interview-guest"
            "interview-host"
            "similar-videos"
            "third-party-genre"
          ]
        ) {
          id
          contentReferenceType {
            type
          }
          contentTo {
            id
            contentId
            contentType
            title
            published
            titleGroupId
          }
        }
      }
    }
  }
`

export const GET_COURSE_MODULE_CONTENT = gql`
  query GetCourseModuleContent($id: ID!) {
    contentList(where: { id: { eq: $id } }) {
      content {
        id
        contentId
        contentType
        title
        description
        descriptionFormat
        shortDescription
        shortDescriptionFormat
        thirdPartyDescription
        path
        language
        primaryLanguage
        published
        publishedAtDate
        productionStatus
        contentSearchNoIndex
        excludeFromRecommendations
        createdAt
        updatedAt
        productType
        displayType
        seasonNumber
        episodeNumber
        director
        producer
        writer
        studio
        copyright
        instructor
        cast
      }
    }
  }
`

export const GET_COURSE_MODULE_CONTENT_REFERENCES = gql`
  query GetCourseModuleContentReferences($id: ID!, $language: [String]!) {
    contentList(where: { id: { eq: $id } }) {
      content {
        id
        contentReferencesTo(types: ["course-module-practice", "course-module-other"]) {
          id
          contentReferenceType {
            type
          }
          sortKey
          contentFrom(language: $language) {
            id
            contentId
            contentType
            title
            published
            imageContentRelationships(
              language: $language
              types: ["d6-keyart-with-text", "d6-keyart-no-text"]
            ) {
              id
              type
              image {
                id
                url
              }
            }
          }
        }
      }
    }
  }
`

export const GET_PARENT_COURSE_ID = gql`
  query GetParentCourseId($id: ID!) {
    contentList(where: { id: { eq: $id } }) {
      content {
        id
        contentReferencesFrom(types: ["course-module"]) {
          id
          contentTo {
            id
            contentId
            contentType
            title
          }
        }
      }
    }
  }
`

export const GET_SERIES_REFERENCES = gql`
  query GetSeriesWithLatestEpisodeAdminCategory($seriesContentId: Int!) {
    contentList(where: { contentId: { eq: $seriesContentId }, contentType: { eq: "series" } }) {
      content {
        contentId
        contentType
        title
        language
        episodeNumber
        seasonNumber
        producer
        writer
        episodesCount
        seasonsCount
        director
        instructor
        studio
        copyright
        cast
        productType
        contentReferencesFrom {
          id
          fromContentContentId
          fromContentContentType
          toContentContentId
          toContentContentType
          contentTo {
            id
            contentId
            contentType
            subType
            bundle
            title
          }
        }
      }
    }
  }
`

export const GET_PRODUCT_REFERENCES = gql`
  query GetProductReferences($productContentId: Int!) {
    contentList(where: { contentId: { eq: $productContentId }, contentType: { eq: "product" } }) {
      content {
        contentId
        contentType
        title
        language
        contentReferencesFrom {
          id
          fromContentContentId
          fromContentContentType
          toContentContentId
          toContentContentType
          contentFrom {
            id
            contentId
            contentType
            title
            language
            published
            path
          }
          contentTo {
            id
            contentId
            contentType
            title
            language
            published
            path
          }
        }
        contentReferencesTo {
          id
          fromContentContentId
          fromContentContentType
          toContentContentId
          toContentContentType
          contentFrom {
            id
            contentId
            contentType
            title
            language
            published
            path
          }
          contentTo {
            id
            contentId
            contentType
            title
            language
            published
            path
          }
        }
      }
    }
  }
`

export const UPDATE_COURSE_CONTENT = gql`
  mutation UpdateCourseContent($content: ContentInput!, $generatePath: Boolean) {
    contentUpdate(content: $content, generatePath: $generatePath) {
      content {
        id
        contentId
        contentType
        title
        description
        descriptionFormat
        shortDescription
        shortDescriptionFormat
        thirdPartyDescription
        path
        language
        published
        publishedAtDate
        productionStatus
        contentSearchNoIndex
        excludeFromRecommendations
        createdAt
        updatedAt
        productType
        displayType
        seasonNumber
        episodeNumber
        director
        producer
        writer
        studio
        copyright
        instructor
        cast
        previewMediaId
        featureMediaId
      }
    }
  }
`

export const GET_CONTENT_REFERENCE_TYPES = gql`
  query GetContentReferenceTypes {
    contentReferenceTypesList {
      contentReferenceTypes {
        id
        type
      }
    }
  }
`

export const UPDATE_CONTENT_REFERENCES_SORT_ORDER = gql`
  mutation UpdateContentReferencesSortOrder(
    $contentReferences: [ContentReferenceCreateInput!]!
    $deleteOmittedReferences: Boolean = false
  ) {
    contentReferenceCreateMany(
      contentReferences: $contentReferences
      deleteOmittedReferences: $deleteOmittedReferences
    ) {
      contentReferences {
        id
        sortKey
        fromContentContentId
        fromContentContentType
        toContentContentId
        toContentContentType
      }
    }
  }
`

export const GET_CONTENT_TRANSLATIONS = gql`
  query GetContentTranslations($contentId: Int!, $contentType: String!) {
    contentList(where: { contentId: { eq: $contentId }, contentType: { eq: $contentType } }) {
      content {
        id
        contentId
        contentType
        language
        primaryLanguage
        title
        published
      }
    }
  }
`
